import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { useAuthMe, useRequestSignIn, useVerifySignIn } from '../api/queries';

export function SignInPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const me = useAuthMe();
  const request = useRequestSignIn();
  const verify = useVerifySignIn();

  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // RequireAuth sends bounced visitors here with ?return=/wherever-they-wanted.
  // Drop them back there after successful sign-in. Reject anything that isn't
  // a same-origin pathname to avoid open-redirect.
  const rawReturn = params.get('return');
  const returnTo = rawReturn && rawReturn.startsWith('/') && !rawReturn.startsWith('//')
    ? rawReturn
    : '/dashboard';

  // If a signed link arrived (?email=...#token=...), auto-verify on mount. The token
  // sits in the URL fragment so it never leaks via Referer to cross-origin font/analytics
  // requests; we fall back to the legacy ?token= query for any links still in flight from
  // before the fragment migration (TTL 15 min).
  useEffect(() => {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const token = hashParams.get('token') ?? params.get('token');
    const emailParam = params.get('email');
    if (token && emailParam && !verify.isPending && !verify.isSuccess) {
      verify.mutate(
        { email: emailParam, token },
        {
          onSuccess: () => {
            setParams({});
            window.history.replaceState(null, '', window.location.pathname);
            navigate(returnTo);
          },
          onError: (err) => {
            const ae = err as ApiError;
            setErrorMessage(messageFromError(ae));
            setParams({});
            window.history.replaceState(null, '', window.location.pathname);
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (me.data) {
      navigate(returnTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const onRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      const res = await request.mutateAsync(email);
      setMaskedEmail(res.maskedEmail);
      setResendCooldown(30);
    } catch (err) {
      setErrorMessage(messageFromError(err as ApiError));
    }
  };

  const onVerifyCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      await verify.mutateAsync({ email, code });
      navigate(returnTo);
    } catch (err) {
      setErrorMessage(messageFromError(err as ApiError));
    }
  };

  if (verify.isPending && (params.get('token') || window.location.hash.includes('token='))) {
    return (
      <article>
        <h1>Signing you in…</h1>
        <p>Verifying your magic link.</p>
      </article>
    );
  }

  return (
    <article>
      <h1>Sign in</h1>
      <p className="muted">No password. We'll email you a 6-digit code and a one-tap link.</p>
      {errorMessage && (
        <p className="banner banner-warn" role="alert">
          {errorMessage}
        </p>
      )}

      {!maskedEmail ? (
        <form onSubmit={onRequest} noValidate>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              inputMode="email"
            />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={request.isPending || !email}>
            {request.isPending ? 'Sending…' : 'Send my sign-in code'}
          </button>
        </form>
      ) : (
        <>
          <p className="banner banner-info">We sent a code to <strong>{maskedEmail}</strong>.</p>
          <form onSubmit={onVerifyCode} noValidate>
            <div className="field">
              <label htmlFor="code">6-digit code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                maxLength={6}
                required
                aria-describedby="code-hint"
              />
              <span id="code-hint" className="hint">Or just click the link in the email. It'll bring you back here automatically.</span>
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={verify.isPending || code.length !== 6}>
              {verify.isPending ? 'Verifying…' : 'Verify code'}
            </button>
          </form>
          <p style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn"
              onClick={(e) => {
                e.preventDefault();
                if (resendCooldown === 0) {
                  void onRequest({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>);
                }
              }}
              disabled={resendCooldown > 0 || request.isPending}
              aria-live="polite"
            >
              {resendCooldown > 0
                ? `Resend in ${formatCooldown(resendCooldown)}`
                : 'Resend code'}
            </button>
          </p>
        </>
      )}
    </article>
  );
}

function formatCooldown(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function messageFromError(err: ApiError): string {
  if (err.reason === 'rate-limited') {
    return "You've requested too many codes. Please wait a few minutes and try again.";
  }
  if (err.reason === 'token-already-used') {
    return 'That code has already been used. Request a fresh one.';
  }
  if (err.reason === 'token-expired') {
    return 'That code or link has expired. Request a fresh one.';
  }
  if (err.reason === 'invalid-token') {
    return "That code doesn't match. Double-check what you typed.";
  }
  if (err.fieldErrors?.email) {
    return err.fieldErrors.email[0];
  }
  return err.message ?? 'Sign-in failed.';
}
